/**
 * UGREEN 全局 Quick View
 * 触发：`[data-ug-quick-add]` + `data-ug-quick-add-url`（兼容 charge-promo 属性）
 * API：`window.UGREEN.quickAdd.open(url, { title, variantId, module })`
 * 埋点：统一走 window.ugGa4Track.quickViewEvent（select_item / view_item）
 * morph 后须恢复弹层壳 id/class。
 */
(function () {
  'use strict';

  if (window.UGREEN && window.UGREEN.quickAdd && window.UGREEN.quickAdd.__inited) {
    return;
  }

  const UG = (window.UGREEN = window.UGREEN || {});

  /**
   * 解析 Quick View 埋点上下文
   * - module：来源板块（collection / search …）
   * - button_name：交互形态（热区 quick_view / 按钮 buy_now）
   * @param {HTMLElement | null | undefined} trigger
   * @param {string} [moduleHint]
   * @returns {{ module: string, button_name: string }}
   */
  function resolveQuickViewTrackContext(trigger, moduleHint) {
    let module = String(moduleHint || '').trim();
    if (!module && trigger instanceof HTMLElement) {
      const host = trigger.closest('[data-ga4-module]');
      if (host instanceof HTMLElement) {
        module = String(host.getAttribute('data-ga4-module') || '').trim();
      }
    }
    if (!module) module = 'quick_view';
    const button_name = trigger instanceof HTMLButtonElement ? 'buy_now' : 'quick_view';
    return { module, button_name };
  }

  /** 把来源 module 写到弹层根，供弹层内 click_button 统一取用 */
  function stampQuickViewModule(dialogEl, module) {
    if (!(dialogEl instanceof HTMLElement)) return;
    const mod = String(module || '').trim();
    if (mod) dialogEl.setAttribute('data-ga4-module', mod);
    else dialogEl.removeAttribute('data-ga4-module');
  }

  /** Quick View 打开成功：优先 quickViewEvent，缺失时回退 viewItem */
  function trackQuickViewOpen(trigger, moduleHint) {
    const track = window.ugGa4Track;
    if (!track || !(trigger instanceof HTMLElement)) return;
    const items = typeof track.itemsFromElement === 'function' ? track.itemsFromElement(trigger) : [];
    if (!items.length) return;
    const ctx = resolveQuickViewTrackContext(trigger, moduleHint);
    const currency = track.getCurrency ? track.getCurrency() : 'USD';
    const value = track.calcItemsValue ? track.calcItemsValue(items) : 0;
    if (typeof track.quickViewEvent === 'function') {
      track.quickViewEvent({
        action: 'open',
        module: ctx.module,
        button_name: ctx.button_name,
        currency,
        value,
        items,
      });
      return;
    }
    if (typeof track.viewItem === 'function') {
      track.viewItem({
        module: ctx.module,
        position: ctx.module,
        button_name: ctx.button_name,
        currency,
        value,
        items,
      });
    }
  }

  /** Quick View 触发点击：优先 quickViewEvent，缺失时回退 selectItem */
  function trackQuickViewSelectItem(trigger, moduleHint) {
    const track = window.ugGa4Track;
    if (!track || !(trigger instanceof HTMLElement)) return;
    const items = typeof track.itemsFromElement === 'function' ? track.itemsFromElement(trigger) : [];
    if (!items.length) return;
    const ctx = resolveQuickViewTrackContext(trigger, moduleHint);
    const currency = track.getCurrency ? track.getCurrency() : 'USD';
    if (typeof track.quickViewEvent === 'function') {
      track.quickViewEvent({
        action: 'select',
        module: ctx.module,
        button_name: ctx.button_name,
        currency,
        items,
      });
      return;
    }
    if (typeof track.selectItem === 'function') {
      track.selectItem({
        module: ctx.module,
        button_name: ctx.button_name,
        currency,
        items,
      });
    }
  }

  /** @type {{ cleanupOnce: () => void; dispose: () => void } | null} */
  let activeOpenSession = null;

  /** @type {HTMLElement[]} */
  let suppressedHoverZones = [];

  /** 关闭弹层后短暂忽略卡片点击，避免 click-through 立刻再次打开 Quick View */
  let suppressQuickAddClickUntil = 0;

  function armQuickAddClickSuppress(durationMs = 350) {
    suppressQuickAddClickUntil = Date.now() + durationMs;
  }

  /** @returns {HTMLDialogElement | null} */
  function getQuickAddNativeDialog() {
    const dialogEl = document.getElementById('quick-add-dialog');
    const nativeDialog = dialogEl?.querySelector('dialog');
    return nativeDialog instanceof HTMLDialogElement ? nativeDialog : null;
  }

  /** @returns {boolean} */
  function isQuickAddDialogOpen() {
    const nativeDialog = getQuickAddNativeDialog();
    return Boolean(nativeDialog && nativeDialog.open);
  }

  /** 仍有顶层弹层（含手机 class 底栏）时不要拆滚动锁 */
  function hasBlockingOverlay() {
    if (document.querySelector('dialog[open]')) return true;
    return Boolean(
      document.querySelector(
        'dialog.ug-key-benefits__dialog.is-open, dialog.ug-advantage-dialog.is-open, dialog.ug-key-benefits__dialog.is-sheet-active, dialog.ug-advantage-dialog.is-sheet-active'
      )
    );
  }

  /** 与主题 dialog.js 对齐：QV / 权益底栏打开时锁背景滚动 */
  function lockPageScroll(on) {
    if (on) {
      document.documentElement.setAttribute('scroll-lock', '');
      return;
    }
    if (hasBlockingOverlay()) return;
    document.documentElement.removeAttribute('scroll-lock');
  }

  function lockQuickAddScroll(on) {
    lockPageScroll(on);
  }

  /**
   * 同步关闭 Quick View：只调 native dialog.close()。
   * 不走主题异步 closeDialog（等动画），否则再次打开会被未结束的旧 closeDialog 关掉。
   */
  function closeQuickAddModal() {
    cleanupUgQaPortals();

    const dialogEl = document.getElementById('quick-add-dialog');
    const nativeDialog = getQuickAddNativeDialog();
    if (!(nativeDialog instanceof HTMLDialogElement)) return;

    if (dialogEl instanceof HTMLElement && typeof dialogEl.__ugClearOpenListeners === 'function') {
      dialogEl.__ugClearOpenListeners();
    }

    armQuickAddClickSuppress(350);
    nativeDialog.classList.remove('dialog-closing');

    const wasOpen = nativeDialog.open;
    if (wasOpen) {
      try {
        nativeDialog.close();
      } catch (_) {}
    }

    /* 仅在确实关过打开态时通知会话 cleanup，避免空关误伤正在打开的会话 */
    if (wasOpen && dialogEl instanceof HTMLElement) {
      try {
        dialogEl.dispatchEvent(new CustomEvent('dialog:close', { bubbles: true }));
      } catch (_) {}
    }
  }

  /**
   * 同步关闭 + 自管遮罩/ESC 监听，避免覆盖主题 closeDialog 后监听器叠加
   * @param {HTMLElement} dialogEl
   */
  function wireDialogCloseControls(dialogEl) {
    if (!(dialogEl instanceof HTMLElement) || dialogEl.__ugCloseWired) return;
    dialogEl.__ugCloseWired = true;

    /** @type {AbortController | null} */
    let openListenersAbort = null;

    const clearOpenListeners = () => {
      if (openListenersAbort) {
        openListenersAbort.abort();
        openListenersAbort = null;
      }
    };
    dialogEl.__ugClearOpenListeners = clearOpenListeners;

    dialogEl.showDialog = function ugShowQuickAddDialog() {
      const nativeDialog = dialogEl.querySelector('dialog');
      if (!(nativeDialog instanceof HTMLDialogElement)) return;
      if (nativeDialog.open) return;

      clearOpenListeners();
      nativeDialog.classList.remove('dialog-closing');
      nativeDialog.showModal();
      if (nativeDialog.hasAttribute('scroll-lock')) {
        lockQuickAddScroll(true);
      }
      try {
        dialogEl.dispatchEvent(new CustomEvent('dialog:open', { bubbles: true }));
      } catch (_) {}

      openListenersAbort = new AbortController();
      const { signal } = openListenersAbort;

      dialogEl.addEventListener(
        'click',
        (event) => {
          if (!(event instanceof MouseEvent)) return;
          /* 点遮罩（dialog 自身）关闭 */
          if (event.target === nativeDialog) {
            closeQuickAddModal();
          }
        },
        { signal }
      );

      dialogEl.addEventListener(
        'keydown',
        (event) => {
          if (!(event instanceof KeyboardEvent) || event.key !== 'Escape') return;
          /* 子层卖点/权益/放大开着时，ESC 只关子层 */
          if (
            document.querySelector(
              'dialog.ug-key-benefits__dialog[open], dialog.ug-key-benefits__dialog.is-open, dialog.ug-advantage-dialog[open], dialog.ug-advantage-dialog.is-open, zoom-dialog[data-ug-qa-zoom-dialog] dialog[open]'
            )
          ) {
            return;
          }
          event.preventDefault();
          closeQuickAddModal();
        },
        { signal }
      );
    };

    dialogEl.closeDialog = () => {
      clearOpenListeners();
      closeQuickAddModal();
    };
  }

  let quickAddCloseGuardBound = false;

  /** 关闭后短暂抑制热区重开（首页整卡热区 click-through） */
  function bindQuickAddCloseGuard() {
    if (quickAddCloseGuardBound) return;
    quickAddCloseGuardBound = true;

    document.addEventListener(
      'close',
      (event) => {
        const nativeDialog = getQuickAddNativeDialog();
        if (event.target === nativeDialog) {
          armQuickAddClickSuppress(350);
        }
      },
      true
    );
  }

  /** 清除浏览器 sticky :hover（弹层关闭后卡片 hover 残留） */
  function clearStickyHover(el) {
    if (!(el instanceof HTMLElement)) return;
    const parent = el.parentElement;
    if (!parent) return;
    const prev = parent.style.pointerEvents;
    parent.style.pointerEvents = 'none';
    requestAnimationFrame(() => {
      parent.style.pointerEvents = prev;
    });
  }

  /** 卡片热区打开 Quick View 时抑制 hover 展示 */
  function suppressZoneHover(zone) {
    if (!(zone instanceof HTMLElement)) return;
    zone.classList.add('is-qv-hover-suppressed');
    if (!suppressedHoverZones.includes(zone)) {
      suppressedHoverZones.push(zone);
    }
    clearStickyHover(zone);
  }

  function releaseSuppressedHover() {
    suppressedHoverZones.forEach((zone) => {
      if (zone.isConnected) {
        zone.classList.remove('is-qv-hover-suppressed');
        clearStickyHover(zone);
      }
    });
    suppressedHoverZones = [];
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  }

  /** 打开序号：防止连点/切换商品时旧请求回写污染当前弹层 */
  let openSeq = 0;

  const CACHE_MAX = 10;
  /** 手机底栏下滑收尾时长，与 ug-short-description / ug-advantage-list 的 0.32s transition 对齐 */
  const SHEET_CLOSE_ANIM_MS = 320;
  /** LRU：键为规范化 URL，值为商详 grid 节点 */
  /** @type {Map<string, Element>} */
  const contentCache = new Map();

  /** 进行中的拉取（预取与打开共用，避免重复请求） */
  /** @type {Map<string, Promise<Element>>} */
  const inflightFetches = new Map();

  let prefetchTimer = 0;
  /** @type {string} */
  let prefetchPendingKey = '';

  /** @type {Readonly<{ triggers: string; zones: string; urlAttrs: string[]; titleAttrs: string[]; ignore: string }>} */
  const DEFAULT_BIND = {
    triggers: '[data-ug-quick-add], [data-na-charge-promo-buy]',
    zones:
      '[data-ug-quick-add-zone], [data-nnp-product-cta], [data-na-charge-promo], [data-na-editions-cta]',
    urlAttrs: ['data-ug-quick-add-url', 'data-charge-promo-product-url'],
    titleAttrs: ['data-ug-quick-add-title', 'data-charge-promo-product-title'],
    ignore:
      '[data-ug-quick-add-ignore], .ug-related-product-check, [data-ug-card-features-toggle], .ug-product-card__features-overlay, [data-ug-copy-data], .copy-button[data-clipboard-text], .atc_buttons_box, .ug-col-rec-card__atc-wrap, .ug-index-section-ProductList__atc-wrap, product-form-component, .ug-product-form-component, .ug-quick-view-atc-wrap, [data-ug-key-benefits-open], [data-ug-advantage-open]',
  };

  /*
   * 常规 Quick View：
   * 显示：图、标题/标签、价格、折扣码、规格、加购、快速结账、库存、ETA、星级、五点描述、权益列表、
   *       Worry-free 主题条、View details
   * 隐藏：Klarna、相关推荐、Trust、倒计时、完整 Review 等
   */
  const QA_COLLAPSE_MATCHERS = [
    '.view-product-title',
    '#product-countdown',
    '.ug-countdown',
    '.ug-related-products',
    '.product__trust-divAll',
    '.jdgm-review-widget',
    '#judgeme_product_reviews',
    '.jdgm-rev-widg',
    '[id*="judge_me_product_reviews"]',
    '[id*="__ug_countdown"]',
    '[id*="__ug_product_related"]',
    '[id*="__ug_trust_badge"]',
    '[id*="hextom_sales_boost"]',
    '[id*="lai_product_reviews"]',
    '[id*="worry_free"]',
    '[id*="aiod_discount"]',
    '[id*="kite_discount"]',
    '[class*="kl_promo"]',
    /* Quick View 内仍隐藏 Klarna（morph 后 locale/SDK 不稳定） */
    'klarna-placement',
    '[id*="klarna"]',
    '[class*="klarna"]',
  ];

  /**
   * 详情列白名单：必须显示的模块（含异步晚到内容）
   * 空壳清理不得误收这些节点及其 shopify-block 父级
   */
  const QA_KEEP_CHILD_MATCH =
    'product-form-component, product-price, product-inventory, .discount-code-component, .ug-highlight-tag, .ug-product-eta, .ug-product-short-desc-wrap, .ug-advantage-list, .worry-free-banner, .buy-buttons-block, .ug-product-atc, .ug-qa-learn-more, rte-formatter, .text-block, variant-picker, .variant-picker, .view-more-details__wrapper, .jdgm-preview-badge, .jdgm-prev-badge, .ug-product-card__rating, [id*="__ug_product_eta"], [id*="__ug_short_description"], [id*="__ug_advantage_list"], [id*="__ug_product_discount"], [id*="__ug_product_subtitle"]';

  /**
   * @param {Element | null | undefined} el
   * @param {string[]} attrNames
   * @returns {string}
   */
  function readAttr(el, attrNames) {
    if (!(el instanceof HTMLElement)) return '';
    for (const name of attrNames) {
      const value = el.getAttribute(name)?.trim();
      if (value) return value;
    }
    return '';
  }

  /**
   * 规范化商品 URL：同源 pathname + variant，去掉 utm/hash，便于缓存命中
   * @param {string} raw
   * @param {string | number} [variantId]
   * @returns {{ cacheKey: string; fetchUrl: string }}
   */
  function normalizeProductUrl(raw, variantId) {
    const fallback = String(raw || '').trim();
    try {
      const u = new URL(fallback, window.location.origin);
      if (variantId != null && String(variantId) !== '') {
        u.searchParams.set('variant', String(variantId));
      }
      const variant = u.searchParams.get('variant') || '';
      const path = u.pathname.replace(/\/+$/, '') || u.pathname;
      const cacheKey = `${u.origin}${path}${variant ? `?variant=${encodeURIComponent(variant)}` : ''}`;
      /* 实际请求保留原查询中的 variant，其余营销参数去掉以减小页面体积 */
      const fetchUrl = `${u.origin}${u.pathname}${variant ? `?variant=${encodeURIComponent(variant)}` : ''}`;
      return { cacheKey, fetchUrl };
    } catch (_) {
      return { cacheKey: fallback, fetchUrl: fallback };
    }
  }

  /**
   * @param {string} key
   * @returns {Element | undefined}
   */
  function cacheGet(key) {
    if (!key || !contentCache.has(key)) return undefined;
    const value = contentCache.get(key);
    /* 访问后挪到末尾，保持 LRU */
    contentCache.delete(key);
    if (value) contentCache.set(key, value);
    return value;
  }

  /**
   * @param {string} key
   * @param {Element} value
   */
  function cacheSet(key, value) {
    if (!key || !(value instanceof Element)) return;
    if (contentCache.has(key)) contentCache.delete(key);
    contentCache.set(key, value);
    while (contentCache.size > CACHE_MAX) {
      const oldest = contentCache.keys().next().value;
      if (oldest == null) break;
      contentCache.delete(oldest);
    }
  }

  /**
   * 拉取商详 grid（带 inflight 去重 + LRU）
   * @param {string} cacheKey
   * @param {string} fetchUrl
   * @returns {Promise<Element>}
   */
  function ensureProductGrid(cacheKey, fetchUrl) {
    const hit = cacheGet(cacheKey);
    if (hit) return Promise.resolve(hit);

    const existing = inflightFetches.get(cacheKey);
    if (existing) return existing;

    const promise = (async () => {
      const response = await fetch(fetchUrl, { credentials: 'same-origin' });
      if (!response.ok) {
        throw new Error('fetch product page failed');
      }
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const gridElement = html.querySelector('[data-product-grid-content]');
      if (!gridElement) {
        throw new Error('no product grid content');
      }
      const cloned = /** @type {Element} */ (gridElement.cloneNode(true));
      cacheSet(cacheKey, cloned);
      return cloned;
    })().finally(() => {
      if (inflightFetches.get(cacheKey) === promise) {
        inflightFetches.delete(cacheKey);
      }
    });

    inflightFetches.set(cacheKey, promise);
    return promise;
  }

  /**
   * 静默预取（失败忽略，不打断交互）
   * @param {string} rawUrl
   * @param {string | number} [variantId]
   */
  function prefetchProductGrid(rawUrl, variantId) {
    if (!rawUrl) return;
    try {
      const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
      if (conn && conn.saveData) return;
    } catch (_) {}

    const { cacheKey, fetchUrl } = normalizeProductUrl(rawUrl, variantId);
    if (!cacheKey || contentCache.has(cacheKey) || inflightFetches.has(cacheKey)) return;

    ensureProductGrid(cacheKey, fetchUrl).catch(() => {
      /* 预取失败不影响点击打开 */
    });
  }

  /**
   * morph 会按新节点 attributes 覆盖旧根节点，冲掉弹层壳身份
   * @param {HTMLElement} modalContent
   * @param {Element} productGrid
   */
  function restoreModalShell(modalContent, productGrid) {
    if (!(modalContent instanceof HTMLElement)) return;
    modalContent.id = 'quick-add-modal-content';
    modalContent.classList.add('quick-add-modal__content');
    /* 保留商详 grid 修饰类，便于布局覆盖 */
    if (productGrid instanceof Element) {
      productGrid.classList.forEach((cls) => {
        if (cls && !modalContent.classList.contains(cls)) {
          modalContent.classList.add(cls);
        }
      });
      if (productGrid.hasAttribute('data-product-grid-content')) {
        modalContent.setAttribute('data-product-grid-content', '');
      }
    }
  }

  /**
   * 打开后滚回顶部，避免二次打开停在旧滚动位置
   * @param {HTMLElement} modalContent
   * @param {HTMLElement} dialogEl
   */
  function resetQuickAddScroll(modalContent, dialogEl) {
    try {
      modalContent.scrollTop = 0;
      modalContent.scrollLeft = 0;
      const nativeDialog = dialogEl.querySelector('dialog');
      if (nativeDialog instanceof HTMLElement) {
        nativeDialog.scrollTop = 0;
        nativeDialog.scrollLeft = 0;
      }
      dialogEl.scrollTop = 0;
    } catch (_) {}
  }

  /**
   * 可见文本（排除 style/script/template）
   * @param {Element} el
   * @returns {string}
   */
  function visibleText(el) {
    const clone = el.cloneNode(true);
    if (!(clone instanceof Element)) return '';
    clone.querySelectorAll('style, script, template, noscript, link, meta').forEach((n) => n.remove());
    return (clone.textContent || '').replace(/\s+/g, '');
  }

  /**
   * @param {Element} el
   * @returns {boolean}
   */
  function isEffectivelyEmptyBlock(el) {
    if (!(el instanceof HTMLElement)) return true;
    if (el.hasAttribute('data-ug-qa-keep')) return false;
    if (el.getAttribute('data-ug-qa-collapsed') === 'true') return true;

    const kids = Array.from(el.childNodes).filter((node) => {
      if (node.nodeType === Node.COMMENT_NODE) return false;
      if (node.nodeType === Node.TEXT_NODE) return Boolean(node.textContent && node.textContent.trim());
      return true;
    });

    if (kids.length === 0) return true;

    const onlyMeta = kids.every((node) => {
      if (!(node instanceof Element)) return true;
      const tag = node.tagName;
      return tag === 'STYLE' || tag === 'SCRIPT' || tag === 'LINK' || tag === 'META' || tag === 'TEMPLATE' || tag === 'NOSCRIPT';
    });
    if (onlyMeta) return true;

    const hasMedia = Boolean(
      el.querySelector(
        'img, svg, video, iframe, canvas, input, select, textarea, button, a[href], product-form-component, product-price, product-inventory, .variant-option, .discount-code-component, .ug-highlight-tag, .ug-product-eta, .ug-product-short-desc-wrap, .ug-advantage-list, .worry-free-banner, .view-more-details__wrapper, .ug-product-atc, .ug-qa-learn-more'
      )
    );
    if (hasMedia) return false;

    return visibleText(el).length === 0;
  }

  /**
   * @param {Element} el
   * @returns {boolean}
   */
  function shouldKeepQuickAddBlock(el) {
    if (!(el instanceof Element)) return false;
    if (el.hasAttribute('data-ug-qa-keep')) return true;
    try {
      if (el.matches(QA_KEEP_CHILD_MATCH) || el.querySelector(QA_KEEP_CHILD_MATCH)) return true;
    } catch (_) {}
    return false;
  }

  /**
   * @param {ParentNode} root
   */
  function collapseEmptyQuickAddBlocks(root) {
    if (!(root instanceof Element)) return;

    const wrapOf = (el) =>
      el.closest('.shopify-block, [id*="shopify-block"], [id^="shopify-block-"]') || el;

    const expand = (el) => {
      if (!(el instanceof HTMLElement)) return;
      if (el === root || el.id === 'quick-add-modal-content') return;
      el.removeAttribute('data-ug-qa-collapsed');
      el.hidden = false;
      ['display', 'margin', 'padding', 'height', 'min-height', 'overflow'].forEach((prop) => {
        el.style.removeProperty(prop);
      });
    };

    const collapse = (el) => {
      if (!(el instanceof HTMLElement)) return;
      if (el === root || el.id === 'quick-add-modal-content') return;
      /* 白名单模块（及含白名单的父级）永不误收，避免 ETA/五点描述/权益异步晚到后消失 */
      if (shouldKeepQuickAddBlock(el)) return;
      el.setAttribute('data-ug-qa-collapsed', 'true');
      el.hidden = true;
      el.style.setProperty('display', 'none', 'important');
      el.style.setProperty('margin', '0', 'important');
      el.style.setProperty('padding', '0', 'important');
      el.style.setProperty('height', '0', 'important');
      el.style.setProperty('min-height', '0', 'important');
      el.style.setProperty('overflow', 'hidden', 'important');
    };

    /* 先恢复白名单，修正上一轮误折叠 */
    try {
      root.querySelectorAll(QA_KEEP_CHILD_MATCH).forEach((hit) => {
        if (!(hit instanceof HTMLElement)) return;
        expand(hit);
        expand(wrapOf(hit));
      });
    } catch (_) {}

    QA_COLLAPSE_MATCHERS.forEach((sel) => {
      try {
        root.querySelectorAll(sel).forEach((hit) => {
          if (!(hit instanceof Element)) return;
          const wrap = wrapOf(hit);
          /* 隐藏块与白名单同壳时，只收命中节点，不收父级 */
          if (shouldKeepQuickAddBlock(wrap) && wrap !== hit) {
            collapse(hit);
            return;
          }
          collapse(wrap);
          collapse(hit);
        });
      } catch (_) {
        /* :has 等选择器不支持时跳过 */
      }
    });

    /* 无规格的空 picker */
    try {
      root.querySelectorAll('variant-picker, .variant-picker').forEach((picker) => {
        if (!(picker instanceof HTMLElement)) return;
        if (!picker.querySelector('.variant-option')) {
          collapse(wrapOf(picker));
          collapse(picker);
        }
      });
    } catch (_) {}

    const detailsContent =
      root.querySelector('.product-details .group-block-content') || root.querySelector('.product-details');
    if (detailsContent) {
      Array.from(detailsContent.children).forEach((child) => {
        if (!(child instanceof HTMLElement)) return;
        if (shouldKeepQuickAddBlock(child)) {
          expand(child);
          return;
        }
        if (child.getAttribute('data-ug-qa-collapsed') === 'true') return;
        if (isEffectivelyEmptyBlock(child)) collapse(child);
      });
    }
  }

  /**
   * @param {HTMLElement} root
   * @returns {MutationObserver}
   */
  function observeAppInjections(root) {
    let scheduled = 0;
    const observer = new MutationObserver(() => {
      if (scheduled) return;
      scheduled = window.requestAnimationFrame(() => {
        scheduled = 0;
        collapseEmptyQuickAddBlocks(root);
        replaceQuickAddCheckout(root);
      });
    });
    observer.observe(root, { childList: true, subtree: true, characterData: true });
    return observer;
  }

  /**
   * @param {Element} trigger
   * @param {{ zones: string; urlAttrs: string[]; titleAttrs: string[] }} opts
   * @returns {{ url: string; title: string }}
   */
  function resolveQuickAddPayload(trigger, opts) {
    const zone =
      trigger.closest(opts.zones) || (trigger.matches(opts.zones) ? trigger : null) || trigger;

    const url = readAttr(trigger, opts.urlAttrs) || readAttr(zone, opts.urlAttrs);
    const title = readAttr(trigger, opts.titleAttrs) || readAttr(zone, opts.titleAttrs);
    return { url, title };
  }

  /**
   * @returns {Promise<HTMLElement | null>}
   */
  async function getQuickAddDialog() {
    const existing = document.getElementById('quick-add-dialog');
    if (existing && typeof existing.showDialog === 'function') {
      return existing;
    }
    if (customElements.get('quick-add-dialog')) {
      await customElements.whenDefined('quick-add-dialog');
    } else {
      await Promise.race([
        customElements.whenDefined('quick-add-dialog'),
        new Promise((resolve) => {
          window.setTimeout(resolve, 3000);
        }),
      ]);
    }
    const dialogEl = document.getElementById('quick-add-dialog');
    if (dialogEl && typeof dialogEl.showDialog === 'function') {
      return dialogEl;
    }
    return null;
  }

  /* 无通用多语言 Loading 键：只保留转圈，不写死文案 */
  const LOADING_HTML =
    '<div class="ug-quick-add-modal__loading"><span class="ug-quick-add-modal__spinner"></span></div>';

  /**
   * @param {string} productPageUrl
   * @param {{ title?: string; variantId?: string | number; position?: string; module?: string; trigger?: HTMLElement }} [options]
   */
  async function openQuickAddModal(productPageUrl, options = {}) {
    if (!productPageUrl) return false;

    const { cacheKey, fetchUrl } = normalizeProductUrl(productPageUrl, options.variantId);
    if (!cacheKey || !fetchUrl) return false;

    const trackModule =
      options.module ||
      options.position ||
      (options.trigger instanceof HTMLElement
        ? resolveQuickViewTrackContext(options.trigger).module
        : 'quick_view');

    const seq = ++openSeq;

    let modalContent = document.getElementById('quick-add-modal-content');
    const dialogEl = await getQuickAddDialog();
    if (seq !== openSeq) return false;

    if (!modalContent && dialogEl) {
      modalContent = dialogEl.querySelector('.quick-add-modal__content, [data-product-grid-content]');
      if (modalContent instanceof HTMLElement) {
        restoreModalShell(modalContent, modalContent);
      }
    }

    if (!dialogEl || !(modalContent instanceof HTMLElement)) {
      window.location.href = fetchUrl;
      return false;
    }

    /* 供 Learn More 跳当前商品 PDP（含变体） */
    if (dialogEl instanceof HTMLElement) {
      dialogEl.setAttribute('data-ug-qa-pdp-url', fetchUrl);
    }

    try {
      if (activeOpenSession && typeof activeOpenSession.dispose === 'function') {
        activeOpenSession.dispose();
      }
      if (seq !== openSeq) return false;

      /** @type {number[]} */
      const timers = [];
      /** @type {MutationObserver | null} */
      let observer = null;
      let settled = false;
      let closingForCart = false;

      const scrubUiArtifacts = () => {
        if (!(modalContent instanceof HTMLElement)) return;
        modalContent.classList.remove('is-ug-qa-loading');
        modalContent.querySelectorAll('.atc-added').forEach((el) => {
          el.classList.remove('atc-added');
        });
        modalContent.innerHTML = '';
        cleanupUgQaPortals();
        restoreModalShell(modalContent, modalContent);
        document
          .querySelectorAll(
            '.ug-nexode-air-template__charge-promo-buy, [data-ug-quick-add], [data-na-charge-promo-buy]'
          )
          .forEach((el) => {
            if (!(el instanceof HTMLElement)) return;
            el.classList.remove('loading');
            if (el instanceof HTMLButtonElement && !el.classList.contains('is-disabled')) {
              el.disabled = false;
            }
            const prev = el.style.transform;
            el.style.transform = 'translateZ(0)';
            void el.offsetWidth;
            requestAnimationFrame(() => {
              el.style.transform = prev;
            });
          });
      };

      const cleanupOnce = () => {
        if (settled) return;
        settled = true;
        /* 仅作废当前会话，不打断其它商品的预取 inflight */
        if (openSeq === seq) openSeq += 1;
        timers.forEach((id) => window.clearTimeout(id));
        if (observer) {
          observer.disconnect();
          observer = null;
        }
        if (activeOpenSession && activeOpenSession.cleanupOnce === cleanupOnce) {
          activeOpenSession = null;
        }
        /* 关闭后清掉来源 module，避免下次误读到过期值 */
        stampQuickViewModule(dialogEl, '');
        armQuickAddClickSuppress();
        scrubUiArtifacts();
        releaseSuppressedHover();
        if (!closingForCart) {
          document.body.style.overflow = '';
          lockQuickAddScroll(false);
        }
        document.removeEventListener('cart:update', onCartUpdate, true);
      };

      const onCartUpdate = (e) => {
        if (e && e.detail && e.detail.data && e.detail.data.didError) return;
        /* 先关 QV 再让购物车 showModal，避免两个 modal dialog 互抢 */
        closingForCart = true;
        closeQuickAddModal();
      };

      const dispose = () => {
        document.removeEventListener('cart:update', onCartUpdate, true);
        dialogEl.removeEventListener('dialog:close', cleanupOnce);
        const nativeDialog = dialogEl.querySelector('dialog');
        if (nativeDialog instanceof HTMLDialogElement) {
          nativeDialog.removeEventListener('close', cleanupOnce);
        }
        timers.forEach((id) => window.clearTimeout(id));
        if (observer) {
          observer.disconnect();
          observer = null;
        }
        if (activeOpenSession && activeOpenSession.cleanupOnce === cleanupOnce) {
          activeOpenSession = null;
        }
        /* dispose 给新一次 open 让路：不 scrub，由新会话接管内容区 */
      };

      document.addEventListener('cart:update', onCartUpdate, true);
      dialogEl.addEventListener('dialog:close', cleanupOnce, { once: true });
      const nativeDialog = dialogEl.querySelector('dialog');
      if (nativeDialog instanceof HTMLDialogElement) {
        nativeDialog.addEventListener('close', cleanupOnce, { once: true });
      }
      activeOpenSession = { cleanupOnce, dispose };

      restoreModalShell(modalContent, modalContent);
      modalContent.classList.add('is-ug-qa-loading');
      modalContent.innerHTML = LOADING_HTML;
      resetQuickAddScroll(modalContent, dialogEl);
      const nativeForOpen = dialogEl.querySelector('dialog');
      if (nativeForOpen instanceof HTMLDialogElement) {
        nativeForOpen.classList.remove('dialog-closing');
      }
      if (typeof dialogEl.showDialog === 'function') {
        dialogEl.showDialog();
      }
      wireDialogCloseControls(dialogEl);

      const morphMod = await import('@theme/morph');
      if (seq !== openSeq || settled) return false;
      const morph = morphMod && morphMod.morph;
      if (typeof morph !== 'function') {
        throw new Error('morph unavailable');
      }

      const productGrid = await ensureProductGrid(cacheKey, fetchUrl);
      if (seq !== openSeq || settled) return false;
      if (!document.contains(modalContent)) return false;

      /* 始终用缓存副本，避免污染缓存节点 */
      const freshGrid = /** @type {Element} */ (productGrid.cloneNode(true));

      /* morph 前再校验一次，避免切换商品时旧会话写穿 */
      if (seq !== openSeq || settled) return false;
      morph(modalContent, freshGrid);
      if (seq !== openSeq || settled) return false;
      restoreModalShell(modalContent, freshGrid);
      modalContent.classList.remove('is-ug-qa-loading');
      resetQuickAddScroll(modalContent, dialogEl);

      collapseEmptyQuickAddBlocks(modalContent);
      refreshQuickAddAppEmbeds(modalContent);
      requestAnimationFrame(() => {
        if (seq !== openSeq || settled) return;
        collapseEmptyQuickAddBlocks(modalContent);
        refreshQuickAddAppEmbeds(modalContent);
        resetQuickAddScroll(modalContent, dialogEl);
      });
      observer = observeAppInjections(modalContent);
      timers.push(
        window.setTimeout(() => {
          if (seq !== openSeq || settled) return;
          collapseEmptyQuickAddBlocks(modalContent);
          refreshQuickAddAppEmbeds(modalContent);
        }, 300),
        window.setTimeout(() => {
          if (seq !== openSeq || settled) return;
          collapseEmptyQuickAddBlocks(modalContent);
          refreshQuickAddAppEmbeds(modalContent);
        }, 1000),
        window.setTimeout(() => {
          if (observer) {
            observer.disconnect();
            observer = null;
          }
        }, 4000)
      );

      /* 弹层内 click_button 统一读到来源 module，不再误落成 quick_view */
      stampQuickViewModule(dialogEl, trackModule);

      if (options.trigger instanceof HTMLElement) {
        options.trigger.classList.remove('loading');
        if (options.trigger instanceof HTMLButtonElement && !options.trigger.classList.contains('is-disabled')) {
          options.trigger.disabled = false;
        }
        trackQuickViewOpen(options.trigger, trackModule);
      }

      return true;
    } catch (err) {
      if (seq !== openSeq) return false;
      if (err && err.name === 'AbortError') return false;
      /* 关闭前记下是否应跳转：close 会同步触发 cleanupOnce 抬高 openSeq */
      const shouldFallback = true;
      try {
        if (typeof dialogEl.closeDialog === 'function') dialogEl.closeDialog();
        else if (typeof dialogEl.hideDialog === 'function') dialogEl.hideDialog();
      } catch (_) {}
      if (shouldFallback) {
        window.location.href = fetchUrl;
      }
      return false;
    }
  }

  let globalDelegateBound = false;

  /** Quick View 打开后刷新 Judge.me（商详页 fetch 下来的官方 badge） */
  function refreshQuickAddJudgeMe() {
    if (typeof window.jdgmCacheServer !== 'undefined') {
      if (typeof window.jdgmCacheServer.reloadPreviewBadges === 'function') {
        window.jdgmCacheServer.reloadPreviewBadges();
        return;
      }
      if (typeof window.jdgmCacheServer.renderPreviewBadges === 'function') {
        window.jdgmCacheServer.renderPreviewBadges();
        return;
      }
    }

    if (
      typeof window.jdgm !== 'undefined' &&
      window.jdgm.customWidgets &&
      typeof window.jdgm.customWidgets.init === 'function'
    ) {
      window.jdgm.customWidgets.init();
    }
  }

  function escapeUgHtml(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /** 把弹层挂到 body，避免被 Quick View 的 overflow / 顶层 dialog 裁掉 */
  function portalDialogToBody(dialog) {
    if (!(dialog instanceof HTMLDialogElement) && !(dialog instanceof HTMLElement)) return dialog;
    if (dialog.parentElement === document.body) return dialog;
    dialog.__ugHomeParent = dialog.parentElement;
    dialog.__ugHomeNext = dialog.nextSibling;
    document.body.appendChild(dialog);
    return dialog;
  }

  function restorePortaledDialog(dialog) {
    if (!(dialog instanceof HTMLElement)) return;
    const parent = dialog.__ugHomeParent;
    // 从没搬走过（PDP 原位弹层）不能当孤儿删，否则关 QV 后 PDP 权益/卖点就没了
    if (!parent) return;
    if (document.contains(parent)) {
      if (dialog.__ugHomeNext && dialog.__ugHomeNext.parentNode === parent) {
        parent.insertBefore(dialog, dialog.__ugHomeNext);
      } else {
        parent.appendChild(dialog);
      }
      dialog.__ugHomeParent = null;
      dialog.__ugHomeNext = null;
      return;
    }
    dialog.remove();
  }

  function isSheetDialog(dialog) {
    return (
      dialog instanceof HTMLDialogElement &&
      (dialog.classList.contains('ug-key-benefits__dialog') ||
        dialog.classList.contains('ug-advantage-dialog'))
    );
  }

  function isMobileSheetDialog(dialog) {
    return isSheetDialog(dialog) && window.matchMedia('(max-width: 749px)').matches;
  }

  function shouldIgnoreSheetClose(dialog) {
    return Number(dialog.__ugIgnoreCloseUntil || 0) > Date.now();
  }

  function isExplicitSheetClose(el) {
    return Boolean(
      el instanceof Element &&
        el.closest('.ug-key-benefits__close, .ug-advantage-modal-close-btn, .ug-advantage-modal-content-close')
    );
  }

  function isSheetBackdropTarget(el) {
    if (!(el instanceof Element)) return false;
    if (isExplicitSheetClose(el)) return false;
    return (
      el === el.closest('dialog.ug-key-benefits__dialog, dialog.ug-advantage-dialog') ||
      Boolean(el.closest('.ug-key-benefits__backdrop, .ug-advantage-dialog__overlay'))
    );
  }

  function findOpenQuickAdd() {
    return document.querySelector('.quick-add-modal[open], dialog.quick-add-modal[open]');
  }

  /* QV 已在 top layer：弹层挂到 QV 上，才盖得住内容且不必再 showModal */
  function portalSheetForOpen(dialog) {
    const qv = findOpenQuickAdd();
    if (qv instanceof HTMLElement) {
      if (dialog.parentElement !== qv) {
        if (!dialog.__ugHomeParent) {
          dialog.__ugHomeParent = dialog.parentElement;
          dialog.__ugHomeNext = dialog.nextSibling;
        }
        qv.appendChild(dialog);
      }
      return dialog;
    }
    return portalDialogToBody(dialog);
  }

  /* 挡住打开后同一次点击/幽灵 click 落到遮罩上，避免刚开就关 */
  function armSheetClickGuard(dialog) {
    const until = Date.now() + 600;
    dialog.__ugIgnoreCloseUntil = until;
    const swallow = (event) => {
      if (Date.now() > until) return;
      if (event.target === dialog || isSheetBackdropTarget(event.target)) {
        event.preventDefault();
        event.stopPropagation();
      }
    };
    document.addEventListener('click', swallow, true);
    document.addEventListener('pointerup', swallow, true);
    window.setTimeout(() => {
      document.removeEventListener('click', swallow, true);
      document.removeEventListener('pointerup', swallow, true);
    }, 600);
  }

  function isSheetActive(el) {
    return (
      el instanceof HTMLElement &&
      (el.classList.contains('is-open') ||
        el.classList.contains('is-sheet-active') ||
        (el instanceof HTMLDialogElement && el.open))
    );
  }

  function findActiveSheet(selector) {
    return document.querySelector(`${selector}.is-open, ${selector}.is-sheet-active, ${selector}[open]`);
  }

  function clearSheetCloseTimer(dialog) {
    if (!dialog.__ugSheetCloseTimer) return;
    window.clearTimeout(dialog.__ugSheetCloseTimer);
    dialog.__ugSheetCloseTimer = 0;
  }

  function openNativeSheet(dialog) {
    if (!(dialog instanceof HTMLDialogElement)) return;
    const mobile = isMobileSheetDialog(dialog);
    /* 上一次的收尾动画还挂着就先作废，否则它到期会把刚打开的弹层拆掉并搬回原位 */
    clearSheetCloseTimer(dialog);
    dialog.__ugSheetClosing = false;
    dialog.__ugSheetFinished = false;
    dialog.classList.remove('is-ug-sheet-closing', 'is-ug-sheet-prep');
    portalSheetForOpen(dialog);

    if (mobile) {
      /* 跟购物车权益同一套：不用 show()/showModal()，只摘展示态 + rAF 加 is-open */
      dialog.classList.add('is-sheet-active');
      dialog.classList.remove('is-open');
      lockPageScroll(true);
      armSheetClickGuard(dialog);
      requestAnimationFrame(() => {
        if (!dialog.__ugSheetClosing) dialog.classList.add('is-open');
      });
      return;
    }

    dialog.classList.remove('is-sheet-active', 'is-open');
    if (!dialog.open) {
      try {
        dialog.showModal();
      } catch (_) {
        dialog.setAttribute('open', '');
      }
    }
    lockPageScroll(true);
  }

  function closeNativeDialog(dialog, immediate, force) {
    if (!(dialog instanceof HTMLDialogElement)) return;
    if (!immediate && !force && dialog.__ugSheetClosing) return;
    if (!immediate && !force && shouldIgnoreSheetClose(dialog)) return;

    const finish = () => {
      if (dialog.__ugSheetFinished) return;
      dialog.__ugSheetFinished = true;
      dialog.__ugSheetClosing = false;
      dialog.__ugSheetCloseTimer = 0;
      dialog.classList.remove('is-ug-sheet-closing', 'is-open', 'is-ug-sheet-prep', 'is-sheet-active');
      try {
        if (dialog.open) dialog.close();
      } catch (_) {}
      lockPageScroll(false);
      restorePortaledDialog(dialog);
    };

    /* 手机底栏：先下滑再关，时长对齐购物车权益 0.32s */
    if (!immediate && isMobileSheetDialog(dialog) && isSheetActive(dialog)) {
      dialog.__ugSheetClosing = true;
      dialog.__ugSheetFinished = false;
      dialog.classList.remove('is-open');
      clearSheetCloseTimer(dialog);
      dialog.__ugSheetCloseTimer = window.setTimeout(finish, SHEET_CLOSE_ANIM_MS);
      return;
    }

    clearSheetCloseTimer(dialog);
    dialog.__ugSheetFinished = false;
    finish();
  }

  function isQuickAddHosted(el) {
    return Boolean(
      el instanceof Element && el.closest('.quick-add-modal, #quick-add-dialog, #quick-add-modal-content')
    );
  }

  function cleanupUgQaPortals() {
    document
      .querySelectorAll(
        'dialog.ug-key-benefits__dialog, dialog.ug-advantage-dialog, zoom-dialog[data-ug-qa-zoom-dialog], body > zoom-dialog[data-ug-qa-zoom-dialog]'
      )
      .forEach((el) => {
        if (!(el instanceof HTMLElement)) return;
        const portaled = Boolean(el.__ugHomeParent);
        const onBody = el.parentElement === document.body;
        // PDP 原位弹层没搬过，关 QV 时不要 close/remove
        if (!portaled && !onBody && !isQuickAddHosted(el)) return;
        if (el instanceof HTMLDialogElement) closeNativeDialog(el, true);
        const inner = el.querySelector('dialog');
        if (inner instanceof HTMLDialogElement) closeNativeDialog(inner, true);
        if (el.__ugHomeParent) restorePortaledDialog(el);
      });

    /* 清掉误留在顶层的空 dialog，避免关 QV 后还挡一层白屏 */
    document.querySelectorAll('body > dialog[open], body > zoom-dialog[data-ug-qa-zoom-dialog]').forEach((el) => {
      if (el instanceof HTMLDialogElement) {
        if (el.classList.contains('quick-add-modal')) return;
        if (
          el.classList.contains('ug-key-benefits__dialog') ||
          el.classList.contains('ug-advantage-dialog')
        ) {
          closeNativeDialog(el, true);
          return;
        }
        return;
      }
      if (el instanceof HTMLElement && el.tagName.toLowerCase() === 'zoom-dialog') {
        const inner = el.querySelector('dialog');
        if (inner instanceof HTMLDialogElement) closeNativeDialog(inner, true);
        el.remove();
      }
    });
  }

  /** morph 后给 QV 内弹层换独立 id，避免和 PDP 撞 #ug-advantage-modal */
  function uniquifyQuickAddOverlays(root) {
    if (!(root instanceof Element)) return;
    const stamp = `qv-${Date.now().toString(36)}`;
    root.querySelectorAll('.ug-advantage-list').forEach((list, i) => {
      const key = `${stamp}-adv-${i}`;
      list.setAttribute('data-ug-advantage-list', key);
      const dialog = list.querySelector('dialog.ug-advantage-dialog');
      if (dialog instanceof HTMLElement) {
        dialog.id = `ug-advantage-modal-${key}`;
        dialog.setAttribute('data-ug-advantage-list', key);
      }
    });
    root.querySelectorAll('.ug-key-benefits').forEach((wrap, i) => {
      const key = `${stamp}-kb-${i}`;
      wrap.setAttribute('data-ug-key-benefits', key);
      const dialog = wrap.querySelector('dialog.ug-key-benefits__dialog');
      if (dialog instanceof HTMLElement) {
        dialog.setAttribute('data-ug-key-benefits', key);
      }
    });
  }

  // 元字段里没写 strong / b 时，退而求其次按标记切标题。
  // 行首 [标题] 正文、冒号、破折号（– / —）都是人为写的标题标记；
  // 逗号在正常句子里太常见，只有切出来的标题够短才认，否则宁可不切 ——
  // 不能把 "Compact, lightweight and durable" 切成 "Compact"。
  // 普通连字符 - 不切，避免 USB-C、10-layer 被误伤。
  const BENEFIT_TITLE_SEP_RE = /[:：–—]/;
  const BENEFIT_COMMA_RE = /[,，]/;
  const BENEFIT_COMMA_MAX_TITLE = 40;

  /**
   * 收集 li 内文本节点，便于按字符下标切 DOM。
   * @param {Element} li
   * @returns {{ textNodes: Text[], raw: string } | null}
   */
  function collectUgBenefitText(li) {
    const walker = document.createTreeWalker(li, NodeFilter.SHOW_TEXT);
    /** @type {Text[]} */
    const textNodes = [];
    let raw = '';
    for (let node = walker.nextNode(); node; node = walker.nextNode()) {
      textNodes.push(/** @type {Text} */ (node));
      raw += node.nodeValue || '';
    }
    if (!raw.trim()) return null;
    return { textNodes, raw };
  }

  /**
   * 从 cut 之后克隆剩余 HTML 作为正文。
   * @param {Element} li
   * @param {Text[]} textNodes
   * @param {number} cutExclusive 正文起点的全局下标
   * @returns {string}
   */
  function sliceUgBenefitBodyHtml(li, textNodes, cutExclusive) {
    let offset = cutExclusive;
    let target = null;
    for (const node of textNodes) {
      const len = (node.nodeValue || '').length;
      if (offset <= len) {
        target = node;
        break;
      }
      offset -= len;
    }
    if (!target) return '';

    const range = document.createRange();
    range.selectNodeContents(li);
    range.setStart(target, offset);
    const holder = document.createElement('div');
    holder.appendChild(range.cloneContents());
    return holder.innerHTML.trim();
  }

  /**
   * 行首 [标题] 切标题；方括号本身不进标题。正文可为空。
   * @param {Element} li
   * @returns {{ title: string, body: string } | null}
   */
  function splitUgBenefitAtBrackets(li) {
    const collected = collectUgBenefitText(li);
    if (!collected) return null;
    const { textNodes, raw } = collected;
    const start = raw.search(/\S/);
    if (start < 0 || raw.charAt(start) !== '[') return null;
    const close = raw.indexOf(']', start + 1);
    if (close < 0) return null;
    const title = raw.slice(start + 1, close).trim();
    if (!title) return null;
    return { title, body: sliceUgBenefitBodyHtml(li, textNodes, close + 1) };
  }

  /**
   * 在第一个分隔符处把 li 切成标题和正文。标题取纯文本，正文保留原有行内标签。
   * 切不出（没分隔符 / 标题为空 / 正文为空）时返回 null，交回原来的「整条当标题」逻辑。
   * @param {Element} li
   * @returns {{ title: string, body: string } | null}
   */
  function splitUgBenefitAtSeparator(li) {
    const collected = collectUgBenefitText(li);
    if (!collected) return null;
    const { textNodes, raw } = collected;

    let cut = raw.search(BENEFIT_TITLE_SEP_RE);
    if (cut < 0) {
      const comma = raw.search(BENEFIT_COMMA_RE);
      if (comma < 0 || raw.slice(0, comma).trim().length > BENEFIT_COMMA_MAX_TITLE) return null;
      cut = comma;
    }

    const title = raw.slice(0, cut).trim();
    if (!title || !raw.slice(cut + 1).trim()) return null;

    return { title, body: sliceUgBenefitBodyHtml(li, textNodes, cut + 1) };
  }

  function stripLeadingBenefitSepHtml(html) {
    const holder = document.createElement('div');
    holder.innerHTML = html || '';
    const walker = document.createTreeWalker(holder, NodeFilter.SHOW_TEXT);
    let node = walker.nextNode();
    while (node) {
      const next = walker.nextNode();
      const cleaned = String(node.nodeValue || '').replace(/^[\s\u00a0]*[:：–—,，]+[\s\u00a0]*/, '');
      if (cleaned) {
        node.nodeValue = cleaned;
        break;
      }
      node.remove();
      node = next;
    }
    return holder.innerHTML.trim();
  }

  /** @param {Element} root */
  function parseUgKeyBenefits(root) {
    if (!(root instanceof Element)) return;
    const source = root.querySelector('[data-ug-key-benefits-source]');
    const preview = root.querySelector('[data-ug-key-benefits-preview]');
    const detail = root.querySelector('[data-ug-key-benefits-detail]');
    if (!source || !preview || !detail || preview.getAttribute('data-parsed') === '1') return;

    const items = source.querySelectorAll('li');
    if (!items.length) {
      preview.setAttribute('data-parsed', '1');
      return;
    }

    const html = Array.prototype.map
      .call(items, (li) => {
        let title = '';
        let body = '';
        const heading = li.querySelector('strong, b');
        if (heading) {
          title = (heading.textContent || '').replace(/[:：–—]\s*$/, '').trim();
          const clone = li.cloneNode(true);
          const cloneHeading = clone.querySelector('strong, b');
          if (cloneHeading) cloneHeading.remove();
          body = (clone.innerHTML || '').trim();
        } else {
          const split = splitUgBenefitAtBrackets(li) || splitUgBenefitAtSeparator(li);
          title = split ? split.title : (li.textContent || '').trim();
          body = split ? split.body : '';
        }
        body = stripLeadingBenefitSepHtml(body);
        return (
          '<li class="ug-key-benefits__item">' +
          '<p class="ug-key-benefits__item-title">' +
          escapeUgHtml(title) +
          '</p>' +
          (body ? '<div class="ug-key-benefits__item-body">' + body + '</div>' : '') +
          '</li>'
        );
      })
      .join('');

    preview.innerHTML = html;
    detail.innerHTML = html;
    preview.setAttribute('data-parsed', '1');
  }

  /** @param {ParentNode} [root] */
  function initUgShortDescription(root) {
    const scope = root instanceof Element ? root : document;
    scope.querySelectorAll('.ug-key-benefits').forEach(parseUgKeyBenefits);
  }

  UG.initKeyBenefits = initUgShortDescription;

  function findKeyedDialog(owner, attr, selector) {
    if (!(owner instanceof Element)) return null;
    const local = owner.querySelector(selector);
    if (local instanceof HTMLDialogElement) return local;
    const key = owner.getAttribute(attr);
    if (!key) return null;
    return document.querySelector(`${selector}[${attr}="${CSS.escape(key)}"]`);
  }

  function toggleKeyBenefitsDialog(root, shouldOpen) {
    if (!(root instanceof Element)) return;
    const dialog = findKeyedDialog(root, 'data-ug-key-benefits', 'dialog.ug-key-benefits__dialog');
    if (!(dialog instanceof HTMLDialogElement)) return;
    if (shouldOpen) {
      openNativeSheet(dialog);
      return;
    }
    closeNativeDialog(dialog);
  }

  function openAdvantageDialog(btn) {
    if (!(btn instanceof HTMLElement)) return;
    const list = btn.closest('.ug-advantage-list');
    const dialog = findKeyedDialog(list, 'data-ug-advantage-list', 'dialog.ug-advantage-dialog');
    if (!(dialog instanceof HTMLDialogElement)) return;

    const titleEl = dialog.querySelector('[data-ug-advantage-modal-title]');
    const bodyEl = dialog.querySelector('[data-ug-advantage-modal-body]');
    if (titleEl) titleEl.textContent = btn.dataset.title || '';
    if (bodyEl) {
      const tpl = btn.querySelector('template');
      bodyEl.innerHTML = tpl ? tpl.innerHTML : '';
    }
    openNativeSheet(dialog);
  }

  function closeAdvantageDialog(fromEl) {
    const dialog =
      (fromEl instanceof Element && fromEl.closest('dialog.ug-advantage-dialog')) ||
      findActiveSheet('dialog.ug-advantage-dialog');
    if (!(dialog instanceof HTMLDialogElement)) return;
    closeNativeDialog(dialog);
  }

  function bindSheetBreakpointWatcher() {
    if (window.__ugSheetBreakpointBound) return;
    window.__ugSheetBreakpointBound = true;
    const mq = window.matchMedia('(max-width: 749px)');
    const onChange = () => {
      document
        .querySelectorAll('dialog.ug-key-benefits__dialog, dialog.ug-advantage-dialog')
        .forEach((dialog) => {
          if (isSheetActive(dialog)) closeNativeDialog(dialog, true, true);
        });
    };
    if (typeof mq.addEventListener === 'function') mq.addEventListener('change', onChange);
    else if (typeof mq.addListener === 'function') mq.addListener(onChange);
  }

  function bindUgProductOverlays() {
    if (window.__ugProductOverlaysBound) return;
    window.__ugProductOverlaysBound = true;
    bindSheetBreakpointWatcher();

    document.addEventListener('click', (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const kbOpen = target.closest('[data-ug-key-benefits-open]');
      const kbClose = target.closest('[data-ug-key-benefits-close]');
      if (kbOpen || kbClose) {
        const root = (kbOpen || kbClose).closest('.ug-key-benefits');
        if (kbOpen && root) {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();
          toggleKeyBenefitsDialog(root, true);
          return;
        }
        if (kbClose) {
          event.preventDefault();
          const dialog = kbClose.closest('dialog.ug-key-benefits__dialog');
          if (dialog instanceof HTMLDialogElement) {
            closeNativeDialog(dialog, false, isExplicitSheetClose(kbClose));
          } else if (root) {
            toggleKeyBenefitsDialog(root, false);
          }
        }
        return;
      }

      if (target.classList.contains('ug-key-benefits__dialog') && target instanceof HTMLDialogElement) {
        closeNativeDialog(target);
        return;
      }

      const advOpen = target.closest('[data-ug-advantage-open]');
      if (advOpen instanceof HTMLElement) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        openAdvantageDialog(advOpen);
        return;
      }

      const advClose = target.closest('[data-ug-advantage-dialog-close]');
      if (advClose) {
        event.preventDefault();
        const dialog =
          advClose.closest('dialog.ug-advantage-dialog') ||
          findActiveSheet('dialog.ug-advantage-dialog');
        if (dialog instanceof HTMLDialogElement) {
          closeNativeDialog(dialog, false, isExplicitSheetClose(advClose));
        }
        return;
      }

      if (target.classList.contains('ug-advantage-dialog') && target instanceof HTMLDialogElement) {
        closeNativeDialog(target);
      }
    });

    document.addEventListener(
      'close',
      (event) => {
        const dialog = event.target;
        if (!(dialog instanceof HTMLDialogElement)) return;
        if (
          dialog.classList.contains('ug-key-benefits__dialog') ||
          dialog.classList.contains('ug-advantage-dialog')
        ) {
          dialog.classList.remove('is-open');
          /* 留在 body，避免下次打开再搬节点造成卡顿 */
        }
      },
      true
    );

    document.addEventListener(
      'cancel',
      (event) => {
        const dialog = event.target;
        if (!isSheetDialog(dialog)) return;
        /* 拦住浏览器自带的点遮罩/ESC 立即关闭 */
        event.preventDefault();
        if (dialog.__ugSheetClosing || shouldIgnoreSheetClose(dialog)) return;
        closeNativeDialog(dialog);
      },
      true
    );
  }

  /** @param {ParentNode} [root] */
  function scheduleUgShortDescriptionInit(root) {
    const scope = root instanceof Element ? root : document;
    const run = () => initUgShortDescription(scope);
    run();
    requestAnimationFrame(run);
  }

  function getQuickAddLearnMoreLabel() {
    const dialog = document.getElementById('quick-add-dialog');
    const label = dialog instanceof HTMLElement ? dialog.getAttribute('data-learn-more-label') : '';
    return (label || '').trim();
  }

  /**
   * Learn More 指向当前弹层商品 PDP，带当前选中变体。
   * @param {Element} root
   * @returns {string}
   */
  function resolveQuickAddPdpHref(root) {
    const dialog = document.getElementById('quick-add-dialog');
    const stored = dialog instanceof HTMLElement ? dialog.getAttribute('data-ug-qa-pdp-url') || '' : '';
    const variantInput = root.querySelector(
      '.buy-buttons-block input[name="id"], product-form-component input[name="id"], input[name="id"]'
    );
    const variantId =
      variantInput instanceof HTMLInputElement || variantInput instanceof HTMLSelectElement
        ? String(variantInput.value || '').trim()
        : '';
    if (stored) {
      const { fetchUrl } = normalizeProductUrl(stored, variantId);
      return fetchUrl || stored;
    }
    const more = root.querySelector('.view-more-details[href], .view-more-details a[href], a.view-more-details');
    if (more instanceof HTMLAnchorElement && more.getAttribute('href')) {
      const { fetchUrl } = normalizeProductUrl(more.getAttribute('href') || '', variantId);
      return fetchUrl || more.href;
    }
    return variantId ? `${window.location.pathname}?variant=${encodeURIComponent(variantId)}` : '';
  }

  /**
   * Quick View 去掉 Shop Pay / 钱包 / Or 分隔，改成 Learn More 去 PDP。
   * @param {ParentNode} root
   */
  function replaceQuickAddCheckout(root) {
    if (!(root instanceof Element)) return;
    const formButtons = root.querySelector('.product-form-buttons, .buy-buttons-block');
    if (!formButtons) return;

    formButtons
      .querySelectorAll(
        '.ug-product-checkout, .ug-product-checkout-divider, shopify-accelerated-checkout, shopify-accelerated-checkout-cart, .shopify-payment-button, [data-shopify="payment-button"]'
      )
      .forEach((el) => el.remove());

    let link = formButtons.querySelector('a.ug-qa-learn-more');
    if (!(link instanceof HTMLAnchorElement)) {
      link = document.createElement('a');
      link.className = 'ug-qa-learn-more';
      link.setAttribute('data-ug-qa-keep', '');
      link.innerHTML =
        '<span></span><svg viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="M7.5 5l5 5-5 5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';
      const buyNow = formButtons.querySelector('.ug-product-buy-now');
      if (buyNow instanceof HTMLElement) {
        buyNow.insertAdjacentElement('afterend', link);
      } else {
        formButtons.appendChild(link);
      }
      const syncHref = () => {
        const href = resolveQuickAddPdpHref(root);
        if (href) link.setAttribute('href', href);
      };
      /* pointerdown 先于默认跳转，保证带上当前变体；也保留 cmd/ctrl 新开页 */
      link.addEventListener('pointerdown', syncHref);
      link.addEventListener('click', syncHref);
    }

    const label = getQuickAddLearnMoreLabel();
    const labelEl = link.querySelector('span');
    if (labelEl && labelEl.textContent !== label) labelEl.textContent = label;
    const href = resolveQuickAddPdpHref(root);
    if (href && link.getAttribute('href') !== href) link.setAttribute('href', href);
  }

  /**
   * morph 后的 deferred-media 可能尚未升级，声明式 on:click 会失效。
   * 同时去掉视频滑块上的放大点击，避免点封面打开 zoom 而不是播放。
   */
  function ensureDeferredMediaDefined() {
    if (customElements.get('deferred-media')) return Promise.resolve();
    const script = document.querySelector('script[src*="media.js"]');
    if (script instanceof HTMLScriptElement && script.src) {
      return import(script.src).catch(function () {});
    }
    return Promise.race([
      customElements.whenDefined('deferred-media'),
      new Promise(function (resolve) {
        window.setTimeout(resolve, 1500);
      }),
    ]);
  }

  /** @param {HTMLElement} mediaEl */
  function activateDeferredMedia(mediaEl) {
    if (typeof mediaEl.showDeferredMedia === 'function') {
      mediaEl.showDeferredMedia();
      return;
    }

    const existingVideo = mediaEl.querySelector('video');
    if (mediaEl.getAttribute('data-media-loaded') === 'true') {
      if (existingVideo instanceof HTMLVideoElement) {
        existingVideo.muted = true;
        existingVideo.setAttribute('playsinline', '');
        existingVideo.play().catch(function () {});
      }
      return;
    }

    const tpl = mediaEl.querySelector('template');
    const raw = tpl && tpl.content && tpl.content.firstElementChild;
    if (raw) {
      const content = raw.cloneNode(true);
      mediaEl.setAttribute('data-media-loaded', 'true');
      mediaEl.appendChild(content);
      const playBtn = mediaEl.querySelector('[ref="deferredMediaPlayButton"]');
      if (playBtn instanceof HTMLElement) playBtn.classList.add('deferred-media__playing');
      if (content instanceof HTMLVideoElement) {
        content.muted = true;
        content.setAttribute('playsinline', '');
        content.play().catch(function () {});
      }
      return;
    }

    if (existingVideo instanceof HTMLVideoElement) {
      mediaEl.setAttribute('data-media-loaded', 'true');
      existingVideo.muted = true;
      existingVideo.setAttribute('playsinline', '');
      existingVideo.play().catch(function () {});
    }
  }

  /** @param {ParentNode} [root] */
  function initQuickAddDeferredMedia(root) {
    const scope =
      root instanceof Element ? root : document.getElementById('quick-add-modal-content') || document;
    if (!(scope instanceof Element)) return;

    /* 视频滑块不要走放大图，否则会吃掉播放点击 */
    scope
      .querySelectorAll(
        'slideshow-slide.product-media-container--video, slideshow-slide.product-media-container--external_video, .product-media-container--video, .product-media-container--external_video'
      )
      .forEach(function (slide) {
        if (!(slide instanceof HTMLElement)) return;
        const attr = slide.getAttribute('on:click') || '';
        if (attr.indexOf('zoom-dialog') !== -1 || attr.indexOf('/open/') !== -1) {
          slide.removeAttribute('on:click');
        }
      });

    scope.querySelectorAll('deferred-media').forEach(function (mediaEl) {
      if (!(mediaEl instanceof HTMLElement)) return;
      try {
        customElements.upgrade(mediaEl);
      } catch (_) {}
      if (mediaEl.getAttribute('data-ug-qa-media-inited') === '1') return;
      mediaEl.setAttribute('data-ug-qa-media-inited', '1');

      const playBtn = mediaEl.querySelector(
        '[ref="deferredMediaPlayButton"], .deferred-media__poster-button:not([ref="toggleMediaButton"])'
      );
      const onPlay = function (e) {
        e.preventDefault();
        e.stopPropagation();
        activateDeferredMedia(mediaEl);
      };
      if (playBtn instanceof HTMLElement) {
        playBtn.addEventListener('click', onPlay);
      }
    });
  }

  function refreshQuickAddAppEmbeds(root) {
    const scope =
      root instanceof Element ? root : document.getElementById('quick-add-modal-content') || document;
    replaceQuickAddCheckout(scope);
    refreshQuickAddJudgeMe();
    uniquifyQuickAddOverlays(scope);
    initUgShortDescription(scope);
    void ensureDeferredMediaDefined().then(function () {
      initQuickAddDeferredMedia(scope);
    });
    ensurePdpGalleryNav(scope);
    void initQuickAddZoom(scope);
  }

  /** morph 后重跑缩略图导航的自定义元素定义 */
  function ensurePdpGalleryNav(root) {
    if (!(root instanceof Element)) return;
    const scripts = root.querySelectorAll('script');
    if (!customElements.get('ug-pdp-gallery-nav')) {
      scripts.forEach((old) => {
        if (!(old.textContent || '').includes('ug-pdp-gallery-nav')) return;
        const s = document.createElement('script');
        s.textContent = old.textContent || '';
        document.body.appendChild(s);
        s.remove();
      });
    }
    root.querySelectorAll('ug-pdp-gallery-nav').forEach((el) => {
      if (!(el instanceof HTMLElement)) return;
      try {
        customElements.upgrade(el);
      } catch (_) {}
    });
  }

  function resolveAssetScriptUrl(scope, name) {
    const nodes = [];
    if (scope instanceof Element) nodes.push(...scope.querySelectorAll(`script[src*="${name}"]`));
    nodes.push(...document.querySelectorAll(`script[src*="${name}"]`));
    const hit = nodes.find((el) => el instanceof HTMLScriptElement && el.src);
    return hit instanceof HTMLScriptElement ? hit.src : '';
  }

  function ensureZoomDialogDefined(scope) {
    if (customElements.get('zoom-dialog')) return Promise.resolve();
    const url = resolveAssetScriptUrl(scope, 'zoom-dialog');
    if (url) return import(url).catch(() => {});
    return Promise.race([
      customElements.whenDefined('zoom-dialog'),
      new Promise((resolve) => window.setTimeout(resolve, 1500)),
    ]);
  }

  function ensureDragZoomDefined(scope) {
    if (customElements.get('drag-zoom-wrapper')) return Promise.resolve();
    const url = resolveAssetScriptUrl(scope, 'drag-zoom-wrapper');
    if (url) return import(url).catch(() => {});
    return Promise.resolve();
  }

  /** morph 后升级 zoom-dialog，并给主图重新绑定放大 */
  async function initQuickAddZoom(root) {
    const scope =
      root instanceof Element ? root : document.getElementById('quick-add-modal-content') || document;
    if (!(scope instanceof Element)) return;

    await Promise.all([ensureZoomDialogDefined(scope), ensureDragZoomDefined(scope)]);

    scope.querySelectorAll('zoom-dialog').forEach((el) => {
      if (!(el instanceof HTMLElement)) return;
      const oldId = el.id || 'zoom-dialog';
      const newId = oldId.endsWith('-qv') ? oldId : `${oldId}-qv`;
      el.id = newId;
      el.setAttribute('data-ug-qa-zoom-dialog', '1');
      scope.querySelectorAll('[on\\:click]').forEach((node) => {
        const value = node.getAttribute('on:click') || '';
        if (value.includes(oldId)) {
          node.setAttribute('on:click', value.split(oldId).join(newId));
        }
      });
      try {
        customElements.upgrade(el);
      } catch (_) {}
    });

    scope.querySelectorAll('drag-zoom-wrapper').forEach((el) => {
      if (!(el instanceof HTMLElement)) return;
      try {
        customElements.upgrade(el);
      } catch (_) {}
    });

    scope
      .querySelectorAll(
        'slideshow-slide[on\\:click*="zoom-dialog"], slideshow-slide[on\\:click*="/open/"]'
      )
      .forEach((slide) => {
        if (!(slide instanceof HTMLElement)) return;
        if (
          slide.classList.contains('product-media-container--video') ||
          slide.classList.contains('product-media-container--external_video')
        ) {
          return;
        }
        if (slide.getAttribute('data-ug-qa-zoom') === '1') return;
        const attr = slide.getAttribute('on:click') || '';
        const match = attr.match(/#(zoom-dialog-[^/]+)\/open\/(\d+)/);
        if (!match) return;
        slide.setAttribute('data-ug-qa-zoom', '1');
        slide.removeAttribute('on:click');
        const dialogId = match[1];
        const index = Number(match[2]);
        slide.addEventListener('click', (event) => {
          const clickTarget = event.target;
          if (
            clickTarget instanceof Element &&
            clickTarget.closest(
              'deferred-media, .deferred-media__poster-button, slideshow-arrows, slideshow-controls, button[ref="deferredMediaPlayButton"]'
            )
          ) {
            return;
          }
          const dialog =
            document.getElementById(dialogId) || scope.querySelector(`#${CSS.escape(dialogId)}`);
          if (!(dialog instanceof HTMLElement) || typeof dialog.open !== 'function') return;
          event.preventDefault();
          event.stopPropagation();
          portalDialogToBody(dialog);
          try {
            if (typeof dialog.updatedCallback === 'function') dialog.updatedCallback();
            dialog.open(index, event);
          } catch (err) {
            console.error(err);
          }
        });
      });
  }

  /**
   * 整品或按钮已缺货：不弹 QV，走商详
   * @param {HTMLElement} trigger
   * @param {HTMLElement | null} zone
   * @returns {boolean}
   */
  function isSoldOutQuickAdd(trigger, zone) {
    const nodes = [trigger, zone].filter((el) => el instanceof Element);
    for (const el of nodes) {
      if (el.getAttribute('data-ug-sold-out') === 'true') return true;
      if (el.classList.contains('is-sold-out') || el.classList.contains('ug-pc-card__btn--sold-out')) return true;
      if (el.querySelector('.is-sold-out, .ug-pc-card__btn--sold-out, .ug-related-product-sold-out')) return true;
    }
    return false;
  }

  function shouldIgnoreQuickAddClick(target) {
    if (!(target instanceof Element)) return true;
    try {
      return Boolean(target.closest(DEFAULT_BIND.ignore));
    } catch (_) {
      return false;
    }
  }

  /**
   * @param {Element} target
   * @returns {HTMLElement | null}
   */
  function resolveQuickAddTrigger(target) {
    if (!(target instanceof Element)) return null;

    /* 独立 Quick View 按钮（非卡片热区）优先 */
    const buttonTrigger = target.closest('button[data-ug-quick-add], [data-na-charge-promo-buy]');
    if (buttonTrigger instanceof HTMLElement) {
      return buttonTrigger;
    }

    if (shouldIgnoreQuickAddClick(target)) return null;

    const zone = target.closest(DEFAULT_BIND.zones);
    if (zone instanceof HTMLElement && zone.hasAttribute('data-ug-quick-add')) {
      return zone;
    }

    return null;
  }

  /**
   * @param {HTMLElement} trigger
   * @returns {boolean}
   */
  function isTriggerBusy(trigger) {
    return (
      trigger.classList.contains('is-disabled') ||
      trigger.classList.contains('loading') ||
      (trigger instanceof HTMLButtonElement && trigger.disabled)
    );
  }

  /**
   * @param {Event} e
   */
  async function onDocumentQuickAddClick(e) {
    const target = e.target;
    if (!(target instanceof Element)) return;

    /* 修饰键 / 非主键：放行浏览器默认（新标签打开等） */
    if (e instanceof MouseEvent) {
      if (e.button !== 0) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    }

    /* 关闭抑制期内仍拦截热区默认导航，避免 click-through 进 PDP */
    if (Date.now() < suppressQuickAddClickUntil) {
      const suppressedTrigger = resolveQuickAddTrigger(target);
      if (suppressedTrigger instanceof HTMLElement) {
        e.preventDefault();
        e.stopPropagation();
      }
      return;
    }
    if (isQuickAddDialogOpen()) return;
    if (target.closest('#quick-add-dialog, quick-add-dialog')) return;

    const trigger = resolveQuickAddTrigger(target);
    if (!(trigger instanceof HTMLElement)) return;

    const zone =
      trigger.closest(DEFAULT_BIND.zones) || (trigger.matches(DEFAULT_BIND.zones) ? trigger : null);
    if (zone instanceof HTMLElement && zone.getAttribute('data-cta-use-quickview') === 'false') {
      return;
    }

    if (isTriggerBusy(trigger)) return;

    const { url, title } = resolveQuickAddPayload(trigger, DEFAULT_BIND);
    if (!url) return;

    /* 缺货不弹 QV，跟卡片链接一样进商详 */
    if (isSoldOutQuickAdd(trigger, zone instanceof HTMLElement ? zone : null)) {
      e.preventDefault();
      e.stopPropagation();
      window.location.assign(url);
      return;
    }

    /* 校验通过后再拦截默认行为，避免 busy/无 URL 时吞掉导航 */
    e.preventDefault();
    e.stopPropagation();

    const sourceModule =
      readAttr(trigger, ['data-ga4-module']) || readAttr(zone, ['data-ga4-module']) || '';
    trackQuickViewSelectItem(trigger, sourceModule);

    /* 卡片热区触发：打开前抑制 hover，避免关闭后 ATC 仍停留在 hover 态 */
    const isZoneHotTrigger =
      trigger.hasAttribute('data-ug-quick-add-zone') &&
      trigger.hasAttribute('data-ug-quick-add') &&
      !(trigger instanceof HTMLButtonElement);
    if (isZoneHotTrigger) {
      suppressZoneHover(trigger);
    }

    const useTriggerLoading = trigger instanceof HTMLButtonElement;
    if (useTriggerLoading) {
      trigger.classList.add('loading');
      trigger.disabled = true;
    }
    try {
      const opened = await openQuickAddModal(url, {
        title,
        module: sourceModule,
        position: sourceModule,
        /* 热区也传 trigger，保证 view_item 能取到商品信息 */
        trigger,
      });
      if (!opened) {
        if (isZoneHotTrigger) releaseSuppressedHover();
        if (useTriggerLoading) {
          trigger.classList.remove('loading');
          if (!trigger.classList.contains('is-disabled')) {
            trigger.disabled = false;
          }
        }
      }
    } catch (_) {
      if (isZoneHotTrigger) releaseSuppressedHover();
      if (useTriggerLoading) {
        trigger.classList.remove('loading');
        if (!trigger.classList.contains('is-disabled')) {
          trigger.disabled = false;
        }
      }
    }
  }

  /**
   * 悬停预取：短延迟，避免划过误触发；与打开共用 inflight/缓存
   * @param {Event} e
   */
  function onDocumentQuickAddPointerOver(e) {
    if (!(e instanceof PointerEvent)) return;
    /* 仅鼠标/笔预取，触摸交给点击 */
    if (e.pointerType && e.pointerType !== 'mouse' && e.pointerType !== 'pen') return;

    const target = e.target;
    if (!(target instanceof Element)) return;
    const trigger = resolveQuickAddTrigger(target);
    if (!(trigger instanceof HTMLElement) || isTriggerBusy(trigger)) return;

    const { url } = resolveQuickAddPayload(trigger, DEFAULT_BIND);
    if (!url) return;

    const { cacheKey } = normalizeProductUrl(url);
    if (!cacheKey || contentCache.has(cacheKey) || inflightFetches.has(cacheKey)) return;
    if (cacheKey === prefetchPendingKey) return;

    prefetchPendingKey = cacheKey;
    window.clearTimeout(prefetchTimer);
    prefetchTimer = window.setTimeout(() => {
      prefetchPendingKey = '';
      prefetchProductGrid(url);
    }, 120);
  }

  function initGlobalQuickAdd() {
    if (globalDelegateBound) return;
    globalDelegateBound = true;
    document.addEventListener('click', onDocumentQuickAddClick);
    document.addEventListener('pointerover', onDocumentQuickAddPointerOver, { passive: true });
    bindQuickAddCloseGuard();
    bindUgProductOverlays();
  }

  UG.quickAdd = {
    open: openQuickAddModal,
    prefetch: prefetchProductGrid,
    initShortDescription: initUgShortDescription,
    initKeyBenefits: initUgShortDescription,
    initDeferredMedia: initQuickAddDeferredMedia,
    __inited: true,
  };

  function bootQuickAdd() {
    initGlobalQuickAdd();
    const dialogEl = document.getElementById('quick-add-dialog');
    if (dialogEl instanceof HTMLElement) {
      wireDialogCloseControls(dialogEl);
    }
    scheduleUgShortDescriptionInit(document);
    document.addEventListener('shopify:section:load', (event) => {
      const section = event && event.target;
      scheduleUgShortDescriptionInit(section instanceof Element ? section : document);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootQuickAdd, { once: true });
  } else {
    bootQuickAdd();
  }
})();
